import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactFlow, {
  addEdge,
  ReactFlowProvider,
  MiniMap,
  Controls,
  Background,
} from "reactflow";
import "reactflow/dist/style.css";
import CustomNode from "../../../components/Workflow/CustomNode";
import TopMenuBar from "./TopMenuBar";
import { LateralBar } from "./LateralBar";
import Console from "../../../components/Console";

import workflow from "../../../models/workflow";
import "../../../assets/css/workflow.css";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import comum from "../../../helpers/comum";
import ModalParamRun from "./ModalParamRun";
import funcoes from "../../../models/funcoes";

export default function Workflow() {
  const defaultWidth = 400;
  const minNumber = 0;

  const { idPipeline } = useParams();
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [pipelineTitle, setPipelineTitle] = useState("");

  const [nodesResult, setNodesResult] = useState({});
  const [modalRun, setModalRun] = useState(null);

  const [parametroWidth, setParametroWidth] = useState(minNumber);
  const [activeNode, setActiveNode] = useState(null);
  const [consoleHeight, setConsoleHeight] = useState(35);
  const [consoleText, setConsoleText] = useState("");
  const [load, setLoad] = useState(false);

  const mountPipeline = (form = null) => {
    let finalNodes = nodes;
    if (form) {
      finalNodes = nodes.map((e) => {
        return {
          ...e,
          params: e.params.map((f) => {
            if (form[f.id]) {
              return { ...f, value: form[f.id] };
            }
            return f;
          }),
        };
      });
    }
    return {
      id: idPipeline,
      title: pipelineTitle,
      nodes: finalNodes,
      edges,
    };
  };

  const savePipeline = async () => {
    const body = mountPipeline();
    const res = await workflow.save(body);
    if (res) {
      toast.success("Workflow salvo com sucesso!");
    } else {
      toast.error("Ouve um erro ao tentar salvar o workflow, tente novamente.");
    }
  };

  const _runPipeline = async (form = null) => {
    try {
      setConsoleText(`Iniciou: ${comum.ParseDate()}`);
      setConsoleHeight(250);
      setLoad(true);
      setNodesResult({});
      setModalRun(null);
      const body = mountPipeline(form);
      const res = await workflow.run(body);
      const totalTime = comum.calculateDuration(res.start_date, res.end_date);
      setNodesResult(res.result_functions);
      setConsoleText(
        `Iniciou: ${comum.ParseDate(res.start_date)}\n` +
          `Terminou: ${comum.ParseDate(res.end_date)}\n` +
          `Tempo Decorrido: ${totalTime}\n\n` +
          `${res.console}`
      );
    } catch (error) {
      setConsoleText(`Erro ao executar o pipeline: ${error.message}`);
    } finally {
      setLoad(false);
    }
  };

  const runPipeline = async () => {
    const paramsVazios = nodes
      .map((e) => e.params)
      .flatMap((e) => e)
      .filter((e) => e.type === "input")
      .filter((e) => !e.value || e.value === "");

    if (paramsVazios.length != 0) setModalRun(paramsVazios);
    else await _runPipeline();
  };

  const addNode = (nodeNovo) => {
    setNodes([
      ...nodes,
      {
        ...nodeNovo,
        id: `${nodeNovo.id}_${comum.GenerateId(10)}`,
        position: { x: nodes.length + 10, y: nodes.length + 10 },
      },
    ]);
  };

  async function initPipeline() {
    await Promise.all(
      nodes.map(async (node) => {
        try {
          await setVariablesDynamicVariables(node);
        } catch (error) {
          console.error(
            `Failed to set dynamic variables for node ${node.id}`,
            error
          );
        }
      })
    );
  }

  const getPipeline = async () => {
    const res = await workflow.getPipeline(idPipeline);
    if (!res) {
      toast.error("Erro ao encontrar o workflow.");
      return;
    }

    setNodes(res.nodes);
    setEdges(res.edges);
    setPipelineTitle(res.title);
  };

  const onConnect = useCallback((params) => {
    const newEdge = { ...params, type: "step", animated: true };
    setEdges((eds) => addEdge(newEdge, eds));
  }, []);

  const onNodeDragStop = useCallback((event, node) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === node.id
          ? {
              ...n,
              position: { x: node.position.x, y: node.position.y },
            }
          : n
      )
    );
  }, []);

  const handleMouseDown = (e) => {
    e.preventDefault();
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!activeNode) return;
    const newWidth = Math.min(
      Math.max(window.innerWidth - e.clientX, 290), // Mínimo 290px
      window.innerWidth - defaultWidth // Máximo
    );
    setParametroWidth(newWidth);
  };

  const handleMouseUp = () => {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const getValueOrNone = (obj, key) => {
    if (!obj) return "NONE";

    if (obj && key in obj) {
      return obj[key];
    }

    return "NONE";
  };

  const nodeTypes = useMemo(
    () => ({
      custom: (node) => (
        <CustomNode
          node={node}
          nodeData={nodes.find((nd) => node.id === nd.id)}
          /* The `runStatus` prop in the `CustomNode` component is used to determine the status of the
          node's execution in the workflow. It is passed down to the `CustomNode` component to
          display relevant information based on the node's execution status. */
          runStatus={getValueOrNone(nodesResult, node.id)}
          isActive={node.id === activeNode}
        />
      ),
    }),
    [nodes, nodesResult]
  );

  const onEdgesDelete = useCallback((deletedEdges) => {
    setEdges((eds) =>
      eds.filter(
        (edge) => !deletedEdges.find((deleted) => deleted.id === edge.id)
      )
    );
  }, []);

  const onEdgeClick = useCallback((event, edge) => {
    event.preventDefault();
    const confirmDelete = window.confirm("Deseja remover esta conexão?");
    if (confirmDelete) {
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    }
  }, []);

  const onParamChange = (idNode, idParam, value, dataType = null) => {
    setNodes((nodes) => {
      const final = nodes.map((node) => {
        if (node.id === idNode) {
          return {
            ...node,
            params: node.params.map((param) =>
              param.id === idParam
                ? {
                    ...param,
                    value,
                    dataType: dataType ?? param.dataType,
                  }
                : param
            ),
          };
        }
        return node;
      });
      return final;
    });
  };

  const updateVariableResultparams = async (idNode) => {
    await setVariablesDynamicVariables(nodes.find((e) => e.id === idNode));
  };

  const onRemoveNode = (idNode) => {
    if (window.confirm("Tem certeza que deseja remover esta Função?")) {
      setActiveNode(null);
      setEdges((eds) =>
        eds.filter((edge) => edge.target !== idNode && edge.source !== idNode)
      );

      // Isso é necessario, para que quando um node é removido,
      // também remova os parametros vinculados a ele de outras funções que vinculavam.
      const ids = new Set(
        nodes
          .find((node) => node.id === idNode)
          ?.params.map((param) => param.id) || []
      );

      // Atualiza os nodes, removendo os valores dos parâmetros que estão vinculados ao node removido
      setNodes((currentNodes) =>
        currentNodes.map((node) => ({
          ...node,
          params: node.params.map((param) =>
            ids.has(param.value) ? { ...param, value: "" } : param
          ),
        }))
      );

      setNodes((no) => no.filter((e) => e.id !== idNode));
      setParametroWidth(minNumber);
    }
  };

  const onNodeNameChange = (idNode, value) => {
    setNodes(
      nodes.map((e) => {
        if (idNode === e.id) {
          return {
            ...e,
            data: {
              ...e.data,
              title: value,
            },
          };
        }
        return e;
      })
    );
  };

  const getDynamicResultParamsForeach = async (id, nodes_fo) => {
    // Encontra o parâmetro inicial
    const param = nodes_fo?.params?.find(
      ({ dataType, variable_type, type, value }) =>
        dataType === "select" &&
        variable_type === "array" &&
        type === "input" &&
        value
    );

    if (!param) return; // Retorna cedo se o parâmetro não for encontrado

    // Encontra o nó final com base no valor do parâmetro
    const finalNode = nodes.find((node) =>
      node.params?.some((f) => f.id === param.value)
    );

    if (!finalNode) return; // Retorna cedo se o nó final não for encontrado

    // Extrai o ID final e busca os resultados dinâmicos
    const final_id = finalNode.id.split("_")[0];
    const res = await funcoes.getDynamicResultParams(
      final_id,
      finalNode?.params ?? []
    );

    // Gera os parâmetros finais
    const finalParams = res.map((e) => {
      const myId = comum.GenerateId();
      return {
        dataType: "text",
        id: myId,
        key: myId,
        label: e,
        type: "output",
        variable_type: "text",
      };
    });

    // Atualiza os nós com os novos parâmetros
    setNodes((nodes) =>
      nodes.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            params: [
              ...node.params.filter((f) => f.type === "input" || f.obrigatory),
              ...finalParams,
            ],
          };
        }
        return node;
      })
    );
  };

  const setVariablesDynamicVariables = async (nodes_fo) => {
    switch (nodes_fo?.hide_type) {
      case "foreach":
        return await getDynamicResultParamsForeach(
          nodes_fo.id.split("/")[0],
          nodes_fo
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    const linkElement = document.querySelector(
      "div a[href='https://reactflow.dev']"
    );
    if (linkElement) linkElement.remove();
    getPipeline();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const moveNode = (dx, dy) => {
        setNodes((nds) =>
          nds.map((n) =>
            n.id === activeNode
              ? {
                  ...n,
                  position: {
                    x: n.position.x + dx,
                    y: n.position.y + dy,
                  },
                }
              : n
          )
        );
      };

      switch (event.key) {
        case "ArrowLeft":
          moveNode(-1, 0);
          break;
        case "ArrowRight":
          moveNode(1, 0);
          break;
        case "ArrowUp":
          moveNode(0, -1);
          break;
        case "ArrowDown":
          moveNode(0, 1);
          break;
        default:
          break;
      }
    };

    // Adiciona o listener ao pressionar teclas
    window.addEventListener("keydown", handleKeyDown);

    // Remove o listener quando o componente é desmontado
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeNode, setNodes]);

  return (
    <div className="workflow-container">
      <div
        style={{
          width: `calc(100% - ${parametroWidth}px)`,
        }}
        className="workflow-left-panel"
      >
        <TopMenuBar
          title={pipelineTitle}
          onChange={({ target }) => setPipelineTitle(target.value)}
          onSave={() => savePipeline()}
          onRun={() => runPipeline()}
          load={load}
          onChageFunction={(nodeNovo) => {
            addNode(nodeNovo);
          }}
        />
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onConnect={onConnect}
            onEdgesDelete={onEdgesDelete}
            onEdgeDoubleClick={onEdgeClick}
            onNodeDragStop={onNodeDragStop}
            nodeTypes={nodeTypes}
            fitView
            connectionLineStyle={{
              stroke: "var(--primary)",
              strokeWidth: 4,
            }}
            connectionLineType="step"
            onNodeDragStart={(_, nodeData) => {
              if (!activeNode) {
                setParametroWidth(defaultWidth);
              }
              setActiveNode(nodeData.id);
            }}
          >
            {/* <MiniMap /> */}
            <Controls />
            <Background color="var(--primary)" gap={16} />
          </ReactFlow>
        </ReactFlowProvider>
        <div style={{ height: consoleHeight }}>
          <Console
            result={consoleText}
            setConsoleHeight={setConsoleHeight}
            consoleHeight={consoleHeight}
            minimumHeight={35}
          />
        </div>
      </div>

      <div className="workflow-divider" onMouseDown={handleMouseDown} />

      <LateralBar
        width={activeNode ? parametroWidth : minNumber}
        node={nodes?.find((e) => activeNode && e?.id === activeNode)}
        options={nodes
          ?.filter((node) => (activeNode !== node.id ? node.params : false))
          ?.flatMap((node) => {
            return { node_title: node?.data?.title, ...node.params };
          })}
        minNumberHeight={minNumber}
        onParamChange={onParamChange}
        onNodeNameChange={onNodeNameChange}
        onRemoveNode={onRemoveNode}
        onRefreshParams={updateVariableResultparams}
        onCloseButton={() => {
          setActiveNode(null);
          setParametroWidth(minNumber);
        }}
        onChangeNode={[nodes, setNodes]}
      />

      <ModalParamRun
        modal={modalRun}
        setModal={setModalRun}
        onRun={(form) => _runPipeline(form)}
      />
    </div>
  );
}
